(() => {
  if (customElements.get('bf-picked')) return;
  class BFPicked extends window.BFOfferElement {
    connectedCallback() {
      this.cancelProducts();
      super.connectedCallback();
      const { signal } = this.lifecycle;
      const reconnecting = !!this.track;
      this.carousel = this.querySelector('[data-products-carousel]');
      this.track = this.querySelector('[data-track]');
      this.excludedProductIds = new Set();
      try { JSON.parse(this.dataset.excludedProductIds || '[]').forEach((id) => this.excludedProductIds.add(String(id))); } catch { /* Older cached markup has no exclusions. */ }
      this.search = this.querySelector('[data-search]');
      this.category = this.querySelector('[data-category]');
      this.categoryFilter = this.querySelector('[data-category-filter]');
      this.categoryTrigger = this.querySelector('[data-category-trigger]');
      this.categoryMenu = this.querySelector('[data-category-menu]');
      this.categoryOptions = [...this.querySelectorAll('[data-category-option]')];
      this.selectedCategory = 'default';
      this.search.value = '';
      this.category.value = this.selectedCategory;
      this.setCategoryOpen(false);
      this.syncCategoryMenu();
      this.bindCards();
      if (!reconnecting) this.readPage(this.track);
      this.search.addEventListener('input', () => {
        this.cancelProducts();
        this.loading = true;
        this.reserveCarousel();
        this.querySelector('[data-empty]').hidden = true;
        this.querySelector('[data-load-status]').textContent = this.message('fetchingLabel');
        this.querySelector('[data-retry]').hidden = true;
        this.updateArrows();
        this.filterTimer = setTimeout(() => this.filter(), 300);
      }, { signal });
      this.category.addEventListener('change', () => this.changeCategory(), { signal });
      this.categoryFilter.addEventListener('keydown', (event) => this.onCategoryKeydown(event), { signal });
      this.categoryFilter.addEventListener('focusout', (event) => {
        if (!this.categoryFilter.contains(event.relatedTarget)) this.setCategoryOpen(false);
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.categoryFilter.contains(event.target)) this.setCategoryOpen(false);
      }, { signal });
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-search-form]')) { event.preventDefault(); this.filter(); }
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !this.track.select || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.track.select(event.key === 'Home' ? 0 : this.cards.length - 1, { instant: this.motion() === 'instant' });
        }
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (![...this.category.options].some((option) => option.value === event.detail?.blockId)) return;
        this.category.value = event.detail.blockId;
        this.changeCategory();
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.track);
      this.updateArrows();
      if (reconnecting) this.filter();
    }

    disconnectedCallback() {
      this.cancelProducts();
      this.resizeObserver?.disconnect();
      clearTimeout(this.categoryTypeTimer);
      this.setCategoryOpen(false);
      super.disconnectedCallback();
    }

    cancelProducts() {
      clearTimeout(this.filterTimer);
      this.productRequest?.abort();
      this.productRequest = null;
      this.generation = (this.generation || 0) + 1;
      this.loading = false;
      this.failedRequest = null;
    }

    bindCards() {
      const limit = this.productLimit();
      [...this.track.querySelectorAll('[data-product]')].slice(limit).forEach((card) => card.remove());
      this.cards = Array.from(this.track.querySelectorAll('[data-product]'));
      this.total = this.cards.length;
      this.carousel.hidden = !this.total;
      this.querySelector('[data-empty]').hidden = this.total > 0;
      this.buttons = Array.from(this.querySelectorAll('[data-apply]'));
      this.buttons.forEach((button) => button.disabled = false);
      this.button = this.buttons[0];
      this.renderButton();
    }

    readPage(container) {
      const page = container.querySelector('[data-result-page]');
      if (!page || !/^\d+$/.test(page.dataset.total || '')) throw new Error('Invalid product section');
      const next = page.dataset.nextUrl || '';
      page.remove();
      return next;
    }

    reserveCarousel() {
      const height = this.carousel.getBoundingClientRect().height;
      if (height > 0) this.carousel.style.minHeight = `${height}px`;
      this.carousel.hidden = false;
      this.track.hidden = true;
      this.carousel.querySelector('[data-pagination]')?.setAttribute('hidden', '');
    }

    productLimit() { return Math.min(48, Math.max(3, Math.floor(Number(this.dataset.productLimit) || 12))); }

    changeCategory() {
      this.selectedCategory = this.category.value;
      this.search.value = '';
      this.setCategoryOpen(false);
      this.filter();
    }

    syncCategoryMenu() {
      this.querySelector('[data-category-label]').textContent = this.category.selectedOptions[0]?.textContent || '';
      this.categoryOptions.forEach((option) => option.setAttribute('aria-selected', String(option.dataset.categoryOption === this.category.value)));
    }

    setCategoryOpen(open, focus = false) {
      if (!this.categoryMenu) return;
      clearTimeout(this.categoryTypeTimer);
      this.categoryType = '';
      this.categoryMenu.hidden = !open;
      this.categoryTrigger.setAttribute('aria-expanded', String(open));
      this.categoryOptions.forEach((option) => { option.tabIndex = -1; });
      if (open) {
        const selected = this.categoryOptions.find((option) => option.dataset.categoryOption === this.category.value) ||
          this.categoryOptions.find((option) => option.dataset.categoryOption === this.selectedCategory) || this.categoryOptions[0];
        if (selected) { selected.tabIndex = 0; if (focus) selected.focus(); }
      }
    }

    onCategoryKeydown(event) {
      const open = !this.categoryMenu.hidden;
      if (event.key === 'Escape' && open) {
        event.preventDefault(); event.stopPropagation();
        this.setCategoryOpen(false); this.categoryTrigger.focus({ preventScroll: true }); return;
      }
      if (event.key === 'Tab') { this.setCategoryOpen(false); return; }
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (keys.includes(event.key)) {
        event.preventDefault();
        if (!open) { this.setCategoryOpen(true, true); return; }
        const current = this.categoryOptions.indexOf(document.activeElement);
        let next = event.key === 'Home' ? 0 : event.key === 'End' ? this.categoryOptions.length - 1 :
          (current + (event.key === 'ArrowDown' ? 1 : -1) + this.categoryOptions.length) % this.categoryOptions.length;
        this.categoryOptions.forEach((option, index) => { option.tabIndex = index === next ? 0 : -1; });
        this.categoryOptions[next]?.focus();
      } else if (open && event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        clearTimeout(this.categoryTypeTimer);
        this.categoryType = (this.categoryType || '') + event.key.toLowerCase();
        const match = this.categoryOptions.find((option) => option.lastElementChild.textContent.trim().toLowerCase().startsWith(this.categoryType));
        if (match) {
          this.categoryOptions.forEach((option) => { option.tabIndex = option === match ? 0 : -1; });
          match.focus();
        }
        this.categoryTypeTimer = setTimeout(() => { this.categoryType = ''; }, 600);
      }
    }

    filter() {
      this.cancelProducts();
      const query = this.search.value.trim();
      const searchUrl = new URL(this.dataset.searchUrl, window.location.origin);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');
      if (query) searchUrl.searchParams.set('q', query);
      this.category.value = query ? 'search' : this.selectedCategory;
      this.syncCategoryMenu();
      const option = [...this.category.options].find((item) => item.value === this.selectedCategory);
      const url = query ? searchUrl.href : option.dataset.url;
      return this.loadProducts(url);
    }

    async loadProducts(path) {
      if (this.loading) return false;
      const generation = this.generation;
      const request = new AbortController();
      this.productRequest = request;
      this.loading = true;
      this.failedRequest = null;
      this.track.setAttribute('aria-busy', 'true');
      this.reserveCarousel();
      this.querySelector('[data-empty]').hidden = true;
      this.querySelector('[data-load-status]').textContent = this.message('fetchingLabel');
      this.querySelector('[data-retry]').hidden = true;
      this.updateArrows();
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const ids = new Set();
        const cards = [], visited = new Set();
        const searching = this.category.value === 'search';
        let next = path, base = window.location.origin, doc;
        // At most 50 excluded products and 48 recommendations need two 50-item
        // search pages. A third page tolerates an overlapping response safely.
        for (let page = 0; next && page < 3 && cards.length < this.productLimit(); page++) {
          const url = new URL(next, base);
          if (url.origin !== window.location.origin) throw new Error('Invalid product URL');
          url.searchParams.set('section_id', this.dataset.sectionId);
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          if (visited.has(url.href)) break;
          visited.add(url.href);
          const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Product request failed');
          const html = await response.text();
          if (!this.isConnected || generation !== this.generation) return false;
          doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = [...doc.querySelectorAll('bf-picked')].find((section) => section.dataset.sectionId === this.dataset.sectionId)?.querySelector('[data-track]');
          if (!incoming) throw new Error('Missing product section');
          next = this.readPage(incoming);
          for (const card of incoming.querySelectorAll('[data-product]')) {
            const id = card.dataset.productId;
            if (!id || ids.has(id) || (searching && this.excludedProductIds.has(id))) continue;
            ids.add(id); cards.push(card);
          }
          base = url.href;
          if (!searching) break;
        }
        this.replaceCarousel(cards.slice(0, this.productLimit()), doc);
        this.bindCards();
        this.querySelector('[data-load-status]').textContent = '';
        return true;
      } catch {
        if (this.isConnected && generation === this.generation) {
          this.failedRequest = { path };
          this.querySelector('[data-load-status]').textContent = this.message('fetchError');
          this.querySelector('[data-retry]').hidden = false;
        }
        return false;
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation) {
          this.loading = false;
          this.productRequest = null;
          this.track.removeAttribute('aria-busy');
          this.updateArrows();
        }
      }
    }

    replaceCarousel(cards, doc) {
      // Build in the inert response document before insertion. Native custom
      // elements must upgrade with their complete cells and fresh edge listeners.
      const carousel = doc.createElement('div');
      carousel.className = this.carousel.className;
      carousel.dataset.productsCarousel = '';
      const track = doc.createElement(cards.length ? 'scroll-carousel' : 'div');
      for (const { name, value } of [...this.track.attributes]) {
        if (name !== 'hidden' && name !== 'aria-busy' && name !== 'style') track.setAttribute(name, value);
      }
      track.classList.remove('is-scrollable');
      track.setAttribute('group-cells', '');
      track.setAttribute('allow-drag', '');
      track.setAttribute('cell-selector', '[data-product]');
      track.append(...cards);
      const controls = ['prev', 'next'].map((direction) => {
        const host = doc.createElement(`carousel-${direction}-button`);
        host.setAttribute('aria-controls', track.id);
        const button = this.carousel.querySelector(`[data-${direction}]`).cloneNode(true);
        button.disabled = true;
        host.append(button);
        return host;
      });
      carousel.append(controls[0], track, controls[1]);
      const pagination = doc.createElement('carousel-navigation');
      pagination.className = 'bf-picked__dots page-dots';
      pagination.dataset.pagination = '';
      pagination.setAttribute('aria-controls', track.id);
      pagination.hidden = cards.length < 2;
      cards.forEach((_, index) => {
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'relative';
        button.setAttribute('aria-current', String(index === 0));
        const label = doc.createElement('span');
        label.className = 'sr-only';
        label.textContent = (this.dataset.carouselItemLabel || 'Go to item [index]').replace('[index]', String(index + 1));
        button.append(label);
        pagination.append(button);
      });
      carousel.append(pagination);
      carousel.hidden = !cards.length;
      this.resizeObserver?.disconnect();
      this.carousel.replaceWith(carousel);
      this.carousel = carousel;
      this.track = track;
      this.resizeObserver?.observe(track);
    }

    updateArrows() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-prev]').disabled = this.track.hidden || max < 2 || this.track.scrollLeft <= 2;
      this.querySelector('[data-next]').disabled = this.track.hidden || max < 2 || this.track.scrollLeft >= max - 2;
    }

    motion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }

    move(direction) {
      this.track[direction > 0 ? 'next' : 'previous']?.({ instant: this.motion() === 'instant' });
    }

    onClick(event) {
      if (event.defaultPrevented) return;
      if (event.target.closest('[data-category-trigger]')) {
        this.setCategoryOpen(this.categoryMenu.hidden, true); return;
      }
      const category = event.target.closest('[data-category-option]');
      if (category) {
        this.category.value = category.dataset.categoryOption;
        this.changeCategory();
        this.categoryTrigger.focus({ preventScroll: true }); return;
      }
      super.onClick(event);
      if (event.target.closest('[data-retry]') && this.failedRequest) {
        this.loadProducts(this.failedRequest.path);
      }
      if (event.target.closest('[data-reset]')) {
        this.search.value = ''; this.selectedCategory = 'default'; this.filter(); this.search.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('bf-picked', BFPicked);
})();
